import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { Patient, Practitioner } from '@/lib/types';

// Cart Item Interface - subscription removed from individual items
export interface CartItem {
  program_id: string;
  program_name: string;
  subtitle: string;
  medication_name: string;
  dose: string;
  product_id: string;
  quantity: number;
  price: number;
  base_price: number;
  discount?: number; // Per-unit discount in dollars (max = price - base_price), defaults to 0
  unit_of_measure?: string;
  medication_id: string;
  template_id?: string; // Template ID for program grouping in subscriptions
  sig?: string; // Prescription instructions
  reason?: string; // Reason for prescription
  additionalContext?: string; // Additional context
  refills?: string; // Number of refills
  daysSupply?: string; // Days supply
  flavor?: string; // Flavor for compounded medications
  notes?: string; // Additional notes
  clinic_program_option_id?: string; // Guided clinic program option UUID
  display_mode?: 'medication_select' | 'protocol_showcase';
  product_type?: string | null;
}

// Program-level subscription interface
export interface ProgramSubscription {
  frequency: "weekly" | "monthly" | "quarterly";
  duration?: number; // e.g., months
  autoRenew: boolean;
  discountPercentage?: number; // Subscription discount
}

// Cart State Interface
interface CartState {
  items: CartItem[];
  selectedPatient: Patient | null;
  selectedPractitioner: Practitioner | null;
  isLoading: boolean;
  // Program-level subscription (applies to entire cart/program)
  subscription: ProgramSubscription | null;
  priority: number; // Priority applies to all medications (-1: Low, 1: Normal, 2: Elevated, 3: Urgent)
}

// Cart Actions
type CartAction =
  | { type: 'ADD_TO_CART'; payload: CartItem }
  | { type: 'REMOVE_FROM_CART'; payload: string } // product_id
  | { type: 'UPDATE_QUANTITY'; payload: { product_id: string; quantity: number } }
  | { type: 'SET_ITEM_DISCOUNT'; payload: { product_id: string; discount: number } }
  | { type: 'SET_SUBSCRIPTION'; payload: ProgramSubscription | null }
  | { type: 'SET_PRIORITY'; payload: number }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] }
  | { type: 'LOAD_SUBSCRIPTION'; payload: ProgramSubscription | null }
  | { type: 'LOAD_PRIORITY'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_PATIENT'; payload: Patient | null }
  | { type: 'SET_PRACTITIONER'; payload: Practitioner | null }
  | { type: 'LOAD_SELECTIONS'; payload: { patient: Patient | null; practitioner: Practitioner | null } };

// Cart Context Interface
interface CartContextType {
  items: CartItem[];
  selectedPatient: Patient | null;
  selectedPractitioner: Practitioner | null;
  cartCount: number;
  cartTotal: number;
  cartSubtotal: number;
  subscriptionDiscount: number;
  hasSubscriptions: boolean;
  subscription: ProgramSubscription | null;
  priority: number;
  isLoading: boolean;
  canCheckout: boolean;
  addToCart: (item: CartItem) => void;
  removeFromCart: (product_id: string) => void;
  updateQuantity: (product_id: string, quantity: number) => void;
  setItemDiscount: (product_id: string, discount: number) => void;
  setSubscription: (subscription: ProgramSubscription | null) => void;
  setPriority: (priority: number) => void;
  clearCart: () => void;
  setSelectedPatient: (patient: Patient | null) => void;
  setSelectedPractitioner: (practitioner: Practitioner | null) => void;
}

// Initial State
const initialState: CartState = {
  items: [],
  selectedPatient: null,
  selectedPractitioner: null,
  isLoading: false,
  subscription: null,
  priority: 1, // Default to Normal priority
};

// Cart Reducer
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItemIndex = state.items.findIndex(
        item => item.product_id === action.payload.product_id
      );

      if (existingItemIndex > -1) {
        // Item exists, update quantity
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + action.payload.quantity,
        };
        return { ...state, items: updatedItems };
      } else {
        // New item, add to cart
        return { ...state, items: [...state.items, action.payload] };
      }
    }

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.product_id !== action.payload),
      };

    case 'UPDATE_QUANTITY':
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(item => item.product_id !== action.payload.product_id),
        };
      }
      return {
        ...state,
        items: state.items.map(item =>
          item.product_id === action.payload.product_id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };

    case 'SET_ITEM_DISCOUNT':
      return {
        ...state,
        items: state.items.map(item =>
          item.product_id === action.payload.product_id
            ? { ...item, discount: action.payload.discount }
            : item
        ),
      };

    case 'SET_SUBSCRIPTION':
      return {
        ...state,
        subscription: action.payload,
      };

    case 'SET_PRIORITY':
      return {
        ...state,
        priority: action.payload,
      };

    case 'CLEAR_CART':
      return { 
        ...state, 
        items: [],
        selectedPatient: null,
        selectedPractitioner: null,
        subscription: null,
        priority: 1, // Reset to Normal priority
      };

    case 'LOAD_CART':
      return { ...state, items: action.payload };

    case 'LOAD_SUBSCRIPTION':
      return { ...state, subscription: action.payload };

    case 'LOAD_PRIORITY':
      return { ...state, priority: action.payload };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_PATIENT':
      return { ...state, selectedPatient: action.payload };

    case 'SET_PRACTITIONER':
      return { ...state, selectedPractitioner: action.payload };

    case 'LOAD_SELECTIONS':
      return { 
        ...state, 
        selectedPatient: action.payload.patient,
        selectedPractitioner: action.payload.practitioner,
      };

    default:
      return state;
  }
};

// Create Context
const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'nimbus_cart_items';
const CART_SUB_KEY = 'nimbus_cart_subscription';
const CART_PRIORITY_KEY = 'nimbus_cart_priority';
const CART_SELECTIONS_KEY = 'nimbus_cart_selections';

function loadPersistedState(): Partial<CartState> {
  try {
    const items = localStorage.getItem(CART_STORAGE_KEY);
    const subscription = localStorage.getItem(CART_SUB_KEY);
    const priority = localStorage.getItem(CART_PRIORITY_KEY);
    const selections = localStorage.getItem(CART_SELECTIONS_KEY);
    return {
      items: items ? JSON.parse(items) : [],
      subscription: subscription ? JSON.parse(subscription) : null,
      priority: priority ? JSON.parse(priority) : 1,
      ...(selections ? JSON.parse(selections) : {}),
    };
  } catch {
    return {};
  }
}

// Cart Provider Component
export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    ...initialState,
    ...loadPersistedState(),
  });
  // Dropdown shows for direct-prescribing (medication_select) carts. Missing
  // display_mode is treated as medication_select since that's the DB default.
  const showPractitionerDropdown =
    state.items.length > 0 &&
    !state.items.some(item => item.display_mode === 'protocol_showcase');

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state.items));
      localStorage.setItem(CART_SUB_KEY, JSON.stringify(state.subscription));
      localStorage.setItem(CART_PRIORITY_KEY, JSON.stringify(state.priority));
      localStorage.setItem(CART_SELECTIONS_KEY, JSON.stringify({
        selectedPatient: state.selectedPatient,
        selectedPractitioner: state.selectedPractitioner,
      }));
    } catch {
      // localStorage full or unavailable -- silently degrade
    }
  }, [state.items, state.subscription, state.priority, state.selectedPatient, state.selectedPractitioner]);

  // Cart Actions
  const addToCart = (item: CartItem) => {
    dispatch({ type: 'ADD_TO_CART', payload: item });
  };

  const removeFromCart = (product_id: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: product_id });
  };

  const updateQuantity = (product_id: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { product_id, quantity } });
  };

  const setItemDiscount = (product_id: string, discount: number) => {
    dispatch({ type: 'SET_ITEM_DISCOUNT', payload: { product_id, discount } });
  };

  const setSubscription = (subscription: ProgramSubscription | null) => {
    dispatch({ type: 'SET_SUBSCRIPTION', payload: subscription });
  };

  const setPriority = (priority: number) => {
    dispatch({ type: 'SET_PRIORITY', payload: priority });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem(CART_SUB_KEY);
      localStorage.removeItem(CART_PRIORITY_KEY);
      localStorage.removeItem(CART_SELECTIONS_KEY);
    } catch {
      // silently degrade
    }
  };

  const setSelectedPatient = (patient: Patient | null) => {
    dispatch({ type: 'SET_PATIENT', payload: patient });
  };

  const setSelectedPractitioner = (practitioner: Practitioner | null) => {
    dispatch({ type: 'SET_PRACTITIONER', payload: practitioner });
  };

  // Derived State
  const cartCount = state.items?.length || 0;
  


  // Calculate subtotal (before subscription discounts)
  const cartSubtotal = state.items.reduce((total, item) => total + item.price * (item.quantity || 1), 0);
  
  // Calculate subscription discount (applied to entire cart if subscription is active)
  const subscriptionDiscount = state.subscription?.discountPercentage 
    ? (cartSubtotal * state.subscription.discountPercentage / 100)
    : 0;
  
  // Final total after discounts
  const cartTotal = cartSubtotal - subscriptionDiscount;
  
  // Check if cart has subscription enabled
  const hasSubscriptions = !!state.subscription;
  
  const canCheckout =
    state.items.length > 0 &&
    state.selectedPatient !== null &&
    (!showPractitionerDropdown || state.selectedPractitioner !== null);

  const value: CartContextType = {
    items: state.items,
    selectedPatient: state.selectedPatient,
    selectedPractitioner: state.selectedPractitioner,
    cartCount,
    cartTotal,
    cartSubtotal,
    subscriptionDiscount,
    hasSubscriptions,
    subscription: state.subscription,
    priority: state.priority,
    isLoading: state.isLoading,
    canCheckout,
    addToCart,
    removeFromCart,
    updateQuantity,
    setItemDiscount,
    setSubscription,
    setPriority,
    clearCart,
    setSelectedPatient,
    setSelectedPractitioner,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom Hook
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext; 